/**
 * Parser del Excel de Nuovocell.
 * Estructura esperada:
 *   - Hoja FIFO: catálogo maestro (col A = descripción, fila 4+ tiene productos)
 *   - Hojas de sucursal (MORON, STAND, FREE MARKET, LARA, NUOVOCELL,
 *     YARACAL, GOAJIROS, GRANJA, MIRIMIRE): col A = =FIFO!Ax, col C = costo sucursal
 *
 * Los offsets de fila entre FIFO y cada sucursal varían:
 *   - MORON, STAND, FREE MARKET, LARA: A4 → FIFO!A4 (offset 0)
 *   - NUOVOCELL, YARACAL, GOAJIROS, MIRIMIRE, GRANJA: A5 → FIFO!A4 (offset -1)
 */

const HOJAS_SUCURSAL = [
  { nombre: 'MORON',       offset: 0 },
  { nombre: 'STAND',       offset: 0 },
  { nombre: 'FREE MARKET', offset: 0 },
  { nombre: 'LARA',        offset: 0 },
  { nombre: 'NUOVOCELL',   offset: -1 },
  { nombre: 'YARACAL',     offset: -1 },
  { nombre: 'GOAJIROS',    offset: -1 },
  { nombre: 'MIRIMIRE',    offset: -1 },
  { nombre: 'GRANJA',      offset: -1 },
];

/**
 * Parsea un workbook (objeto SheetJS XLSX) y devuelve costos consolidados.
 * @param {Object} wb - workbook de SheetJS
 * @param {string} sucursalPreferida - nombre de hoja preferida; si tiene costo, se usa.
 *                                     Si no, fallback al primer valor disponible.
 * @returns {Array<{descripcion: string, costoUSD: number, sucursales: string[]}>}
 */
export function parseExcelCostos(wb, sucursalPreferida = 'NUOVOCELL') {
  const XLSX = window.XLSX;
  if (!XLSX) throw new Error('SheetJS no está cargado');

  if (!wb.Sheets.FIFO) {
    throw new Error('El Excel no contiene la hoja "FIFO" (catálogo maestro)');
  }

  // 1. Leer FIFO → mapa fila → descripción
  const fifo = wb.Sheets.FIFO;
  const fifoRange = XLSX.utils.decode_range(fifo['!ref']);
  const catalogo = {}; // {row: desc}
  for (let r = 3; r <= fifoRange.e.r; r++) {
    const cell = fifo[XLSX.utils.encode_cell({ r, c: 0 })];
    if (cell && cell.v && String(cell.v).trim()) {
      catalogo[r + 1] = String(cell.v).trim(); // r+1 porque SheetJS es 0-based, Excel 1-based
    }
  }

  // 2. Leer costos de cada hoja de sucursal
  // Estructura: producto → {sucursal: costo}
  const costosPorProducto = {};
  for (const { nombre, offset } of HOJAS_SUCURSAL) {
    const hoja = wb.Sheets[nombre];
    if (!hoja) continue;
    const range = XLSX.utils.decode_range(hoja['!ref']);
    for (const [filaFifoStr, desc] of Object.entries(catalogo)) {
      const filaFifo = parseInt(filaFifoStr, 10);
      const filaSucursal = filaFifo - offset; // misma lógica del Python
      if (filaSucursal - 1 > range.e.r) continue;
      // Costo está en col C (índice 2)
      const cell = hoja[XLSX.utils.encode_cell({ r: filaSucursal - 1, c: 2 })];
      if (cell && typeof cell.v === 'number' && cell.v > 0) {
        if (!costosPorProducto[desc]) costosPorProducto[desc] = {};
        costosPorProducto[desc][nombre] = cell.v;
      }
    }
  }

  // 3. Consolidar: preferir sucursalPreferida, sino el primero disponible
  const resultado = [];
  for (const [desc, costos] of Object.entries(costosPorProducto)) {
    let costoFinal;
    if (costos[sucursalPreferida] !== undefined) {
      costoFinal = costos[sucursalPreferida];
    } else {
      costoFinal = Object.values(costos)[0];
    }
    resultado.push({
      descripcion: desc,
      costoUSD: costoFinal,
      sucursales: Object.keys(costos),
      todosCostos: costos,
    });
  }

  return {
    productos: resultado,
    totalCatalogoFifo: Object.keys(catalogo).length,
    sucursalesEncontradas: HOJAS_SUCURSAL.filter(h => wb.Sheets[h.nombre]).map(h => h.nombre),
  };
}

/**
 * Normaliza un nombre para matching: sin acentos, minúsculas, separadores unificados.
 *
 * Importante: la "x" solo se trata como separador cuando está entre dígitos
 * (como en "8x256GB" ↔ "8*256GB"). Si la quitáramos siempre, "INFINIX" se
 * convertiría en "INFINI" y romperíamos la detección de marca.
 */
export function normalizarNombre(s) {
  if (!s) return '';
  return String(s)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')  // quitar acentos
    .toLowerCase()
    .replace(/(\d)\s*[x*\/]\s*(\d)/g, '$1 $2')         // 8*256 / 8x256 / 8/256 → 8 256
    .replace(/[\s*\/]+/g, ' ')                          // resto de separadores → espacio
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Cruza productos del Excel contra productos de Sanity.
 * @returns {Object} { matches, dudosos, soloExcel, soloSanity }
 */
export function cruzarProductos(productosExcel, productosSanity) {
  // Índice de Excel
  const excelIdx = new Map();
  for (const p of productosExcel) {
    excelIdx.set(normalizarNombre(p.descripcion), p);
  }

  const matches = [];   // match exacto
  const dudosos = [];   // match parcial — requiere confirmación
  const soloSanity = []; // productos en Sanity sin match en Excel
  const matcheados = new Set();  // descripciones de Excel ya usadas

  for (const ps of productosSanity) {
    const nombreNorm = normalizarNombre(ps.nombre);

    // 1. Match exacto
    if (excelIdx.has(nombreNorm)) {
      const pe = excelIdx.get(nombreNorm);
      matches.push({ sanity: ps, excel: pe, score: 1.0, exacto: true });
      matcheados.add(pe.descripcion);
      continue;
    }

    // 2. Match parcial: contención bidireccional + score
    let mejorScore = 0;
    let mejorMatch = null;
    for (const [ek, pe] of excelIdx.entries()) {
      if (matcheados.has(pe.descripcion)) continue; // evitar reutilizar
      const score = scoreSimilitud(nombreNorm, ek);
      if (score > mejorScore) {
        mejorScore = score;
        mejorMatch = pe;
      }
    }

    if (mejorMatch && mejorScore >= 0.85) {
      // Match casi-seguro: va a matches con flag de revisión
      matches.push({ sanity: ps, excel: mejorMatch, score: mejorScore, exacto: false });
      matcheados.add(mejorMatch.descripcion);
    } else if (mejorMatch && mejorScore >= 0.6) {
      // Match dudoso: requiere confirmación del usuario
      dudosos.push({ sanity: ps, excel: mejorMatch, score: mejorScore, exacto: false });
      // No marcamos como matcheado todavía
    } else {
      soloSanity.push(ps);
    }
  }

  // 3. Excel productos no usados
  const soloExcel = productosExcel.filter(p => !matcheados.has(p.descripcion));

  return { matches, dudosos, soloExcel, soloSanity };
}

/**
 * Score de similitud entre dos strings normalizados.
 * Combina: contención + ratio de tokens compartidos.
 */
function scoreSimilitud(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 1;

  // Contención fuerte
  if (a.includes(b) && b.length >= 6) return 0.9;
  if (b.includes(a) && a.length >= 6) return 0.85;

  // Tokens compartidos
  const ta = new Set(a.split(' ').filter(t => t.length >= 2));
  const tb = new Set(b.split(' ').filter(t => t.length >= 2));
  if (ta.size === 0 || tb.size === 0) return 0;

  let inter = 0;
  for (const t of ta) if (tb.has(t)) inter++;
  const union = ta.size + tb.size - inter;
  return inter / union; // Jaccard
}

// ============= Helpers para crear productos nuevos =============

// Marcas que reconocemos del schema. El orden importa: más específicas primero.
// Nota: incluimos typos comunes del Excel (samsun = samsung, infini = infinix tras normalizar mal).
const MARCAS_CONOCIDAS = [
  { keys: ['xiaomi', 'redmi'],                valor: 'Xiaomi / Redmi' },
  { keys: ['samsung', 'samsun', 'galaxy'],    valor: 'Samsung' },
  { keys: ['apple', 'iphone'],                valor: 'Apple' },
  { keys: ['honor'],                          valor: 'Honor' },
  { keys: ['infinix'],                        valor: 'Infinix' },
  { keys: ['tecno'],                          valor: 'Tecno' },
  { keys: ['itel'],                           valor: 'Itel' },
  { keys: ['huawei'],                         valor: 'Huawei' },
  { keys: ['dell'],                           valor: 'Dell' },
  { keys: ['hp'],                             valor: 'HP' },
  { keys: ['lenovo', 'thinkpad'],             valor: 'Lenovo' },
  // Otros (no en schema pero los detectamos para mostrar; se guarda "Otro" en marca)
  { keys: ['alcatel'],                        valor: 'Otro' },
  { keys: ['logic'],                          valor: 'Otro' },
  { keys: ['olax'],                           valor: 'Otro' },
];

/**
 * Infiere la marca a partir del nombre del producto.
 * Devuelve el valor del schema, o null si no reconoce.
 */
export function inferirMarca(nombre) {
  const n = normalizarNombre(nombre);
  for (const m of MARCAS_CONOCIDAS) {
    for (const k of m.keys) {
      // Match al inicio o como token completo
      if (n.startsWith(k + ' ') || n === k || n.includes(' ' + k + ' ')) {
        return m.valor;
      }
    }
  }
  return null;
}

/**
 * Infiere la categoría. Por ahora todos los productos del Excel son smartphones,
 * pero si el nombre tiene palabras clave de otra categoría las detectamos.
 */
export function inferirCategoria(nombre) {
  const n = normalizarNombre(nombre);
  if (/\b(laptop|notebook|thinkpad|probook|latitude|chromebook)\b/.test(n)) return 'laptops';
  if (/\b(tablet|smart tab|ipad)\b/.test(n)) return 'tablets';
  if (/\b(scanner|escaner|accesori|cargador|cable|funda|cover)\b/.test(n)) return 'accesorios';
  if (/\b(filament)\b/.test(n)) return 'filamentos';
  if (/\b(repuesto|pantalla|bateria|flex|tactil)\b/.test(n)) return 'repuestos';
  if (/\b(modem|router|wifi|olax|portatil)\b/.test(n) && /\b(net|wifi|modem|router|portatil)\b/.test(n)) return 'internet';
  return 'smartphones'; // default razonable para el Excel actual
}

/**
 * Genera un slug URL-safe desde el nombre.
 * No pasa por normalizarNombre (que está optimizado para matching y unifica
 * separadores numéricos). Aquí queremos preservar el nombre tal cual.
 */
export function generarSlug(nombre) {
  return String(nombre || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
}
